import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Sun, Cpu, BatteryCharging, StickyNote, ExternalLink, MessagesSquare, Wrench, Hammer, CheckCircle2, CalendarClock, Navigation } from "lucide-react";

const TIPO = {
  installazione: { label: "Installazione", icon: Hammer, bg: "#E8F5E9", fg: "#1B5E20" },
  intervento: { label: "Intervento", icon: Wrench, bg: "#FFF3E0", fg: "#E65100" },
};

const mapsUrl = (it) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([it.indirizzo, it.cap, it.citta, it.provincia].filter(Boolean).join(" "))}`;

const Row = ({ icon: Icon, children, testid }) => (
  <div className="flex items-start gap-1.5 text-xs" data-testid={testid}>
    <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--text-muted)" }} />
    <div className="min-w-0 break-words">{children}</div>
  </div>
);

export const AgendaCard = ({ item, compact = false, colorBy = "tipo", onReschedule }) => {
  const navigate = useNavigate();
  const t = TIPO[item.tipo];
  const hasCoords = item.coord_lat != null && item.coord_lng != null;
  const accent = colorBy === "squadra" ? (item.squadra_id ? item.squadra_color : "#B30000") : t.fg;
  const indirizzo = [item.indirizzo, item.citta].filter(Boolean).join(", ");
  const materiali = [["Pannelli", item.pannelli, Sun], ["Inverter", item.inverter, Cpu], ["Batteria", item.batteria, BatteryCharging]].filter(([, v]) => v);
  return (
    <div className="card-shell p-3 space-y-2 transition-shadow hover:shadow-md" data-testid={`agenda-card-${item.tipo}-${item.id}`}
      style={{ borderLeft: `4px solid ${accent}` }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: t.bg, color: t.fg }}>
          <t.icon className="w-3 h-3" /> {t.label}
        </span>
        {colorBy === "squadra" && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded truncate max-w-[140px]" data-testid={`agenda-squadra-${item.id}`}
            style={{ background: `${accent}22`, color: accent }}>{item.squadra_name || "Nessuna squadra"}</span>
        )}
        {item.chiuso && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#E8F5E9", color: "#1B5E20" }}><CheckCircle2 className="w-3 h-3" /> Chiuso</span>}
        {(item.ora || item.durata_ore > 0) && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs font-semibold" data-testid={`agenda-ora-${item.id}`}>
            <Clock className="w-3.5 h-3.5" /> {item.ora || "—"}{item.durata_ore > 0 && ` · ${item.durata_ore}h`}
          </span>
        )}
      </div>
      <div>
        <div className="font-bold text-sm leading-tight" style={{ fontFamily: "Manrope" }} data-testid={`agenda-cliente-${item.id}`}>{item.cliente_nome || "Cliente"}</div>
        <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{[item.cliente_ref, item.commessa_nome, colorBy !== "squadra" && item.squadra_name && `Squadra ${item.squadra_name}`].filter(Boolean).join(" · ")}</div>
      </div>
      {indirizzo && (
        <Row icon={MapPin} testid={`agenda-indirizzo-${item.id}`}>
          <a href={mapsUrl(item)} target="_blank" rel="noreferrer" className="underline hover:opacity-80" style={{ color: "var(--primary)" }} data-testid={`agenda-maps-${item.id}`}>{indirizzo}</a>
        </Row>
      )}
      {item.telefono && (
        <Row icon={Phone}><a href={`tel:${item.telefono}`} className="underline" data-testid={`agenda-tel-${item.id}`}>{item.telefono}</a></Row>
      )}
      {!compact && materiali.map(([label, v, Icon]) => <Row key={label} icon={Icon}><b>{label}:</b> {v}</Row>)}
      {item.descrizione && <Row icon={Wrench}>{item.descrizione}</Row>}
      {!compact && item.note && <Row icon={StickyNote}><span className="whitespace-pre-wrap">{item.note}</span></Row>}
      <div className={`flex gap-1.5 pt-1 ${compact ? "flex-col" : "flex-wrap"}`}>
        <button onClick={() => navigate(`/clienti/${item.cliente_id}`)} className="btn-secondary text-xs flex-1 justify-center w-full" data-testid={`agenda-open-cliente-${item.id}`}>
          <ExternalLink className="w-3.5 h-3.5" /> Scheda
        </button>
        <a href={hasCoords ? `https://www.google.com/maps/dir/?api=1&destination=${item.coord_lat},${item.coord_lng}` : undefined}
          target="_blank" rel="noreferrer" aria-disabled={!hasCoords} data-testid={`agenda-navigate-${item.id}`}
          title={hasCoords ? "Naviga alla casa (coordinate esatte)" : "Coordinate non disponibili: salva un Progetto FV per questo cliente"}
          onClick={(e) => { if (!hasCoords) { e.preventDefault(); toast.info("Coordinate non disponibili: salva un Progetto FV per questo cliente"); } }}
          className={`btn-secondary text-xs flex-1 justify-center w-full ${hasCoords ? "" : "opacity-40 cursor-not-allowed"}`}>
          <Navigation className="w-3.5 h-3.5" /> Naviga
        </a>
        <button disabled={!item.chat_id} onClick={() => navigate(`/chat?chat=${item.chat_id}`)} title={item.chat_id ? "Apri chat commessa" : "Nessuna chat attiva"}
          className="btn-secondary text-xs flex-1 justify-center w-full disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`agenda-open-chat-${item.id}`}>
          <MessagesSquare className="w-3.5 h-3.5" /> Chat
        </button>
        {onReschedule && item.tipo === "installazione" && (
          <button onClick={() => onReschedule(item)} className="btn-secondary text-xs flex-1 justify-center w-full" style={{ color: "var(--primary)" }}
            data-testid={`cal-reschedule-${item.pianificazione_id}`}>
            <CalendarClock className="w-3.5 h-3.5" /> Riprogramma
          </button>
        )}
      </div>
    </div>
  );
};
